/**
 * Single source of truth for the PiBo analytics event taxonomy.
 *
 * This module is intentionally dependency-free so it can be imported from both
 * the browser client and the Node server without dragging in either SDK.
 *
 * Add a new event by extending the `PiboEvent` union. Keep names snake_case and
 * past-tense ("thing_happened") to match PostHog conventions.
 */

/** PostHog group type used to aggregate events per organization (family/school). */
export const ORGANIZATION_GROUP = 'organization' as const

/**
 * PostHog group type used to aggregate events per scholarship/sponsorship program.
 * Requires the `program` group type to be added in PostHog project settings (≤5 group types).
 */
export const PROGRAM_GROUP = 'program' as const

export type OrgType = 'family' | 'school' | 'partner' | 'admin'

/** Price tiers from the `price` table (PriceTypeEnum). */
export type PriceType = 'SCHOOL' | 'FAMILY'

/**
 * Seat entitlement source types (mirrors the `seat_source_type` DB enum). Kept as a local
 * string union so this module stays dependency-free (no drizzle import).
 */
export type SeatEntitlementSourceType =
  | 'sponsored_scholarship'
  | 'free_trial'
  | 'pilot'
  | 'internal'
  | 'pemda'
  | 'direct_paid'

/** Grant-family source types — the explicit allow-list that feeds impact/sponsor reporting. */
export const GRANT_FAMILY_SOURCE_TYPES: readonly SeatEntitlementSourceType[] = [
  'sponsored_scholarship',
  'free_trial',
  'pilot',
  'internal',
  'pemda',
]

export function isGrantFamily(sourceType: SeatEntitlementSourceType): boolean {
  return GRANT_FAMILY_SOURCE_TYPES.includes(sourceType)
}

/** Properties attached to the program group (set via group identify). */
export interface ProgramGroupProperties {
  name?: string
  sourceType?: SeatEntitlementSourceType | null
  funderName?: string
  totalSeats?: number
  totalSchools?: number
  totalValue?: number
}

/** Properties attached to the organization group (set via group identify). */
export interface OrganizationGroupProperties {
  name?: string
  orgType?: OrgType
  /** Number of child profiles in the org. Updated on each child_created. */
  childCount?: number
  /** True once the org has at least one child. */
  activated?: boolean
  /** True once the org has a paid, active membership. */
  isPaying?: boolean
  /** Cumulative paid amount (IDR) across all invoices for this org. */
  lifetimeRevenue?: number
  /** True once the school holds at least one active grant-family entitlement. */
  hasEntitlement?: boolean
  /** Source type of the school's most recent active grant-family entitlement. */
  activeEntitlementSource?: SeatEntitlementSourceType
  /** Name of the funder behind the active grant-family entitlement (if any). */
  funderName?: string
  /** Name of the program the school currently participates in (if any). */
  programName?: string
  /** SCHOOL | FAMILY — the active price tier / org kind. */
  planType?: PriceType
  /** Purchased membership seats (incl. add-ons). */
  seats?: number
  /** Count of child members in the org. */
  studentCount?: number
  /** Count of main (teacher/parent) members in the org. */
  teacherCount?: number
  /** Count of classes (teams) in the org. */
  classCount?: number
}

/**
 * Person properties merged onto the PostHog profile via `$set`. Kept
 * dependency-free and optional so callers only send what they have.
 */
export interface PersonProperties {
  userType?: string
  role?: string | null
  gender?: string | null
  /** Whole years, derived from birthDate. */
  age?: number
  /** Free-text grade/class label from the user profile. */
  schoolClass?: string | null
  nickname?: string | null
  emailVerified?: boolean
  phoneVerified?: boolean
  /** Active organization the person is acting in. */
  activeOrganizationId?: string
  orgType?: OrgType
  memberRole?: string
  /** Child has an active Premium seat. */
  isPremium?: boolean
}

/** Discriminated union of every event PiBo emits. */
export type PiboEvent =
  // ── Activation ────────────────────────────────────────────────
  | {
      name: 'user_signed_up'
      props: {
        /**
         * Every field here is optional because the event fires from Better
         * Auth's `user.create.after` hook — the only chokepoint every signup
         * path goes through (email/password, Google, GitHub) — and that hook
         * has no access to the signup form's request context.
         *
         * `persona`, `occupation` and `referralSource` are set as person
         * properties by the account app instead (see `createUserMetadata`), so
         * segment on the person property, not on these.
         */
        persona?: 'parent' | 'teacher'
        occupation?: string
        referralSource?: string
      }
    }
  | {
      name: 'org_created'
      props: {
        organizationId: string
        orgType: OrgType
      }
    }
  | {
      name: 'child_created'
      props: {
        organizationId: string
        childCountAfter: number
        isPremium: boolean
      }
    }
  | {
      name: 'child_logged_in'
      props: {
        organizationId: string
      }
    }
  // ── Learning ──────────────────────────────────────────────────
  | {
      name: 'lesson_started'
      props: {
        learnProgressId: string
        bookId: string
      }
    }
  // ── Content interaction ───────────────────────────────────────
  | {
      name: 'reading_more_than_30s'
      props: {
        sessionId: string
        contentId: string
        durationSeconds: number
        completed: boolean
      }
    }
  // ── School / classroom management ─────────────────────────────
  | {
      name: 'school_class_created'
      props: {
        organizationId: string
        teamId: string
        className?: string
      }
    }
  | {
      name: 'course_created'
      props: {
        organizationId: string
        courseId: string
        lessonModuleId: string
        childCount: number
      }
    }
  // ── Monetization ──────────────────────────────────────────────
  | {
      name: 'checkout_started'
      props: {
        organizationId: string
        invoiceType: 'NEW' | 'RENEWAL' | 'ADD-ON'
        priceType?: PriceType
        seats: number
        totalPrice: number
        currency: 'IDR'
        /**
         * How many of `seats` were assigned to a student during checkout, versus bought empty.
         * Optional because a purchase can carry no assignment at all (e.g. the `apps/admin`
         * server actions that call `createInvoice` without one), and because reach — not
         * payment — is what unlocks premium reading, so this distinction matters for the funnel.
         */
        assignedSeats?: number
        emptySeats?: number
      }
    }
  | {
      name: 'payment_completed'
      props: {
        organizationId: string
        invoiceId: string
        invoiceType: 'NEW' | 'RENEWAL' | 'ADD-ON'
        seats: number
        amount: number
        currency: 'IDR'
        paymentMethod?: string
      }
    }
  // ── Seat entitlements ─────────────────────────────────────────
  | {
      name: 'seat_entitlement_created'
      props: {
        entitlementId: string
        sourceType: SeatEntitlementSourceType
        schoolId: string
        seats: number
        /** Value of the whole bundle in whole rupiah (not per seat). */
        totalValue: number
        funderId?: string
        programId?: string
      }
    }
  | {
      name: 'seat_entitlement_activated'
      props: {
        entitlementId: string
        sourceType: SeatEntitlementSourceType
        schoolId: string
        seats: number
        funderId?: string
        programId?: string
      }
    }
  | {
      name: 'seat_entitlement_seat_allocated'
      props: {
        entitlementId: string
        sourceType: SeatEntitlementSourceType
        schoolId: string
        childId: string
        programId?: string
      }
    }
  | {
      name: 'seat_entitlement_expired'
      props: {
        entitlementId: string
        sourceType: SeatEntitlementSourceType
        schoolId: string
      }
    }
  | {
      name: 'seat_entitlement_revoked'
      props: {
        entitlementId: string
        sourceType: SeatEntitlementSourceType
        schoolId: string
      }
    }
  // ── Content discover / view ───────────────────────────────────
  | {
      name: 'book_collection_discovered'
      props: {
        bookCollectionId: string
        slug?: string
        name?: string
        likesCount?: number
        viewsCount?: number
      }
    }
  | {
      name: 'lesson_module_discovered'
      props: {
        resolvedKind: 'lesson_module' | 'challenge' | 'course'
        lessonModuleId?: string
        challengeId?: string
        courseId?: string
        title?: string
        learningFocus?: string
        estimatedTimeMinutes?: number
        grade?: number
        likesCount?: number
        viewsCount?: number
      }
    }
  | {
      name: 'book_details_viewed'
      props: { bookId: string }
    }
  | {
      name: 'book_opened'
      props: { bookId: string; contentType?: string }
    }
  | {
      name: 'search_performed'
      props: {
        /**
         * What the user did. `query` = typed text, `filter` = facet-only
         * navigation with no search term. Keeping them on one event but
         * discriminated means filter browsing no longer silently pads true
         * search volume (it was 28% of the event).
         */
        trigger: 'query' | 'filter'
        query?: string
        /** Total results across every lane that ran for this search. */
        resultCount: number
        /** Per-lane totals. A lane is absent when it did not run for this search. */
        resultCountByLane?: {
          books?: number
          collections?: number
          practiceModules?: number
          assignmentModules?: number
        }
        filters?: Record<string, unknown>
      }
    }
  | {
      name: 'dashboard_viewed'
      props: {
        dashboardType: 'parent' | 'teacher' | 'school'
        childId?: string
        organizationId?: string
      }
    }
  // ── Learning start ────────────────────────────────────────────
  | {
      name: 'reading_session_started'
      props: {
        bookId: string
        sessionId: string
        learnProgressId?: string
        premiumId?: string
        previousSessionId?: string
      }
    }
  | {
      name: 'self_enrolled'
      props: {
        learnEnrollmentId: string
        enrollmentType: 'challenge' | 'lesson_module'
        challengeId?: string
        lessonModuleId?: string
      }
    }
  | {
      name: 'enrollment_started'
      props: {
        learnEnrollmentId: string
        enrollmentType: 'course' | 'challenge' | 'lesson_module'
        source: 'self' | 'assigned'
        courseId?: string
        challengeId?: string
        lessonModuleId?: string
        startedAt?: string
      }
    }
  | {
      name: 'quiz_started'
      props: {
        learnProgressId: string
        lessonId?: string
        learnEnrollmentId?: string
      }
    }
  // ── Learning completion ───────────────────────────────────────
  | {
      name: 'lesson_completed'
      props: {
        learnProgressId: string
        lessonId?: string
        learnEnrollmentId?: string
        score?: number
        durationSeconds?: number
      }
    }
  | {
      name: 'quiz_submitted'
      props: {
        quizAttemptId: string
        lessonId: string
        learnEnrollmentId: string
        learnProgressId?: string
        score: number
        correctCount: number
        totalQuestions: number
        durationSeconds?: number
        earnedXp?: number
      }
    }
  | {
      name: 'reading_completed'
      props: {
        bookId: string
        sessionId: string
        durationSeconds: number
        lastPageNumber: number
      }
    }
  | {
      name: 'enrollment_completed'
      props: {
        learnEnrollmentId: string
        enrollmentType: 'course' | 'challenge' | 'lesson_module'
        source: 'self' | 'assigned'
        courseId?: string
        challengeId?: string
        lessonModuleId?: string
        timeTotal?: number
      }
    }
  // ── Assignment lifecycle ──────────────────────────────────────
  | {
      name: 'course_assignment_changed'
      props: {
        courseId: string
        lessonModuleId?: string
        addedChildCount: number
        removedChildCount: number
        totalChildCount: number
      }
    }
  // ── School onboarding ─────────────────────────────────────────
  | {
      name: 'class_student_assigned'
      props: {
        childId: string
        teamId: string
        className?: string
        organizationId: string
        origin: 'edit' | 'signup' | 'bulk_add' | 'move'
      }
    }
  // ── Activation & gamification ─────────────────────────────────
  | {
      name: 'child_premium_assigned'
      props: {
        assignedChildCount: number
        childIds: string[]
        membershipId?: string
      }
    }
  | {
      name: 'achievement_earned'
      props: {
        achievementId: string
        achievementType?: string
        childId: string
        organizationId?: string
      }
    }
  | {
      name: 'teacher_invited'
      props: {
        organizationId: string
        invitedEmail: string
        role?: string
      }
    }
  // ── Sambung Sekolah-Rumah (home-school connect) ───────────────
  | {
      /** A school asked a parent, by email, to link their family to one of its students. */
      name: 'home_connect_requested'
      props: {
        requestId: string
        schoolOrganizationId: string
        childId: string
        /** The address already belongs to a PiBo account, so the parent also gets a feed card. */
        parentHasAccount: boolean
      }
    }
  | {
      /**
       * The parent accepted. The child is now home-school connected. `adopt` links the school's
       * student row as-is; `merge` folds it into a child the family already had.
       */
      name: 'home_connect_accepted'
      props: {
        requestId: string
        schoolOrganizationId: string
        familyOrganizationId: string
        /** The child row that survives — the school's row for `adopt`, the family's for `merge`. */
        childId: string
        mode: 'adopt' | 'merge'
      }
    }
  | {
      name: 'home_connect_rejected'
      props: {
        requestId: string
        schoolOrganizationId: string
        childId: string
      }
    }
  | {
      /** The school withdrew a request still waiting for the parent. */
      name: 'home_connect_cancelled'
      props: {
        requestId: string
        schoolOrganizationId: string
        childId: string
      }
    }
  | {
      /** A parent asked a connected child's school to release the child. */
      name: 'home_connect_school_removal_requested'
      props: {
        requestId: string
        schoolOrganizationId: string
        childId: string
        hasMessage: boolean
      }
    }

export type PiboEventName = PiboEvent['name']
